// Back-compat wrapper. All AI traffic goes through useAI -> `ai-features`
// (Google Gemini). Returns the raw text response like the old hook did, and
// now also exposes the parsed JSON so callers can actually use the result.
import { useState } from "react";
import { toast } from "sonner";
import { callAI } from "@/hooks/useAI";

interface AIServiceParams {
  feature: string;
  data?: any;
  query?: string;
  industry?: string;
  userId?: string;
  interactionType?: string;
}

export const useOpenAIService = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<string>("");
  const [json, setJson] = useState<any>(null);

  const callAIService = async (params: AIServiceParams) => {
    setIsLoading(true);
    setResponse("");
    setJson(null);
    try {
      const result = await callAI({
        feature: params.feature,
        input: params.query,
        dataset: params.data,
        industry: params.industry,
        interactionType: params.interactionType,
      });
      if (!result.success) throw new Error(result.error || "AI service failed");
      setResponse(result.response || "");
      setJson(result.json ?? null);
      return result.response;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown AI error";
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /** Same call but returns the full result (json included) and never throws. */
  const callAIServiceRaw = async (params: AIServiceParams) => {
    setIsLoading(true);
    try {
      const result = await callAI({
        feature: params.feature,
        input: params.query,
        dataset: params.data,
        industry: params.industry,
        interactionType: params.interactionType,
      });
      setResponse(result.response || "");
      setJson(result.json ?? null);
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  return { callAIService, callAIServiceRaw, isLoading, response, json, setResponse };
};
